import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { LogOut, ChevronDown, User, Menu, X, RefreshCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import apiService from "@/services/api";
import { API_CONFIG } from "@/config";
import svgPaths from "../../imports/Dashboard/svg-gspmmkprjj";
import NotificationBell from "@/components/NotificationBell";

const IMG_USER_AVATAR = "https://images.unsplash.com/photo-1576533247967-79124db83e48?w=100&q=80";

interface DashboardLayoutProps {
  children: ReactNode;
}

type NavItemProps = {
  icon: ReactNode;
  label: string;
  active?: boolean;
  href?: string;
};

function NavItem({ icon, label, active, href }: NavItemProps) {
  const location = useLocation();

  // Determine if this item is active based on current location
  const isActive = active || (href && location.pathname.startsWith(href.split('/').slice(0, 3).join('/')));

  const content = (
    <div className={`relative shrink-0 w-full cursor-pointer rounded-r-lg transition-all duration-200 ${isActive ? "bg-accent/5" : "hover:bg-[#f2f4f6] hover:translate-x-1"}`}>
      {isActive && (
        <div aria-hidden="true" className="absolute border-[#00543b] border-l-4 border-solid inset-0 pointer-events-none rounded-r-lg" />
      )}
      <div className="flex flex-row items-center size-full">
        <div className={`content-stretch flex items-center ${isActive ? "pl-[12px] pr-[20px]" : "px-[16px]"} py-[12px] relative w-full gap-[10px]`}>
          {icon}
          <div
            className={`flex flex-col font-medium h-[20px] justify-center leading-[0] not-italic relative shrink-0 text-[14px] tracking-[0.35px] ${isActive ? "text-[#00543b]" : "text-[#64748b]"}`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <p className="leading-[20px]">{label}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href} style={{ textDecoration: 'none', display: 'block', width: '100%' }}>
        {content}
      </Link>
    );
  }

  return content;
}

function SideNavBar({ mobile = false }: { mobile?: boolean } = {}) {
  const [walletBalance, setWalletBalance] = useState<string>("£0.00");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWalletBalance = async () => {
      try {
        const response = await apiService.request(API_CONFIG.ENDPOINTS.WALLET_BALANCE, 'GET');
        if (response.success && response.data) {
          const pounds = parseFloat(response.data.balance_pounds || "0.00");
          // Ensure balance never goes negative - show as £0.00 if negative
          const displayBalance = Math.max(0, pounds);
          setWalletBalance(`£${displayBalance.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        }
      } catch (error) {
        console.error('Failed to fetch wallet balance:', error);
        setWalletBalance('£0.00');
      } finally {
        setLoading(false);
      }
    };

    fetchWalletBalance();
  }, []);

const navItems = [
    { icon: <svg fill="none" viewBox="0 0 30 18" width="30" height="18"><path d={svgPaths.p20793584} fill="#00543B" /></svg>, label: "Dashboard", href: "/dashboard" },
    { icon: <svg fill="none" viewBox="0 0 18 18" width="18" height="18"><path d={svgPaths.p33b3fb00} fill="#64748B" /></svg>, label: "Request a Service", href: "/dashboard/request-service" },
    { icon: <svg fill="none" viewBox="0 0 20 18" width="20" height="18"><path d={svgPaths.p130f1800} fill="#64748B" /></svg>, label: "Properties", href: "/dashboard/properties" },
    { icon: <svg fill="none" viewBox="0 0 31 21" width="31" height="21"><path d={svgPaths.p1574ee00} fill="#64748B" /></svg>, label: "Active Orders", href: "/active-orders" },
    { icon: <svg fill="none" viewBox="0 0 30 18" width="30" height="18"><path d={svgPaths.p22876fc0} fill="#64748B" /></svg>, label: "Order History", href: "/dashboard/order-history" },
    { icon: <svg fill="none" viewBox="0 0 34 16" width="34" height="16"><path d={svgPaths.p26835240} fill="#64748B" /></svg>, label: "Payment History", href: "/billing-activity" },
    { icon: <svg fill="none" viewBox="0 0 32.1 20" width="32" height="20"><path d={svgPaths.p3cdadd00} fill="#64748B" /></svg>, label: "Account Settings", href: "/dashboard/settings" },
  ];

  return (
    <div className={`${mobile ? 'flex' : 'hidden lg:flex lg:fixed left-0 top-0 z-10'} bg-white flex-col items-start h-screen w-[288px] border-r border-gray-200`}>
      {/* Brand with gradient logo */}
      <div className="relative shrink-0 w-full">
        <div className="content-stretch flex flex-col gap-[8px] items-start px-[32px] py-[40px] relative w-full">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-sm mb-1">
            <span className="text-white font-bold text-lg">V</span>
          </div>
          <div className="flex flex-col font-bold justify-center leading-[0] not-italic relative shrink-0 text-[#00543b] text-[20px] w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
            <p className="leading-[28px]">Virewire</p>
          </div>
          <div className="flex flex-col font-normal justify-center leading-[0] not-italic relative shrink-0 text-[#94a3b8] text-[10px] tracking-[2px] uppercase w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
            <p className="leading-[15px]">Premium Concierge</p>
          </div>
        </div>
      </div>
      {/* Nav items - scrollable */}
      <div className="flex-1 relative w-full overflow-y-auto">
        <div className="content-stretch flex flex-col gap-[4px] items-start px-[16px] py-[8px] relative w-full">
          {navItems.map((item) => (
            <NavItem key={item.label} icon={item.icon} label={item.label} href={item.href} />
          ))}
        </div>
      </div>
      {/* Wallet Balance - fixed at bottom */}
      <div className="relative shrink-0 w-full border-t border-gray-100">
        <div className="content-stretch flex flex-col items-start p-[24px] relative w-full">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm shrink-0 w-full">
            <div className="content-stretch flex flex-col gap-[8px] items-start p-[16px] relative w-full">
              <div className="flex items-center justify-between w-full mb-1">
                <div className="flex flex-col font-normal justify-center leading-[0] not-italic relative shrink-0 text-[#3f4943] text-[12px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <p className="leading-[16px]">Wallet Balance</p>
                </div>
                <button className="p-1 hover:bg-gray-100 rounded-md transition-colors" title="Refresh balance">
                  <RefreshCw className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              </div>
              {loading ? (
                <div className="space-y-2 w-full">
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4"></div>
                </div>
              ) : (
                <div className="flex flex-col font-bold justify-center leading-[0] not-italic relative shrink-0 text-[#00543b] text-[18px] w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <p className="leading-[28px]">{walletBalance}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopNavBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { user, logout } = useAuth();

  return (
    <div className="fixed top-0 left-0 lg:left-[288px] right-0 bg-white border-b border-gray-200 z-20 flex items-center justify-between" style={{ height: '60px' }}>
      <div className="flex items-center gap-3 flex-1 min-w-0 px-4 md:px-8 lg:px-[32px]">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-foreground text-sm md:text-base truncate">Support & Assistance</h2>
          <p className="text-xs text-muted-foreground hidden sm:block">Available 24/7 via phone or email</p>
        </div>
      </div>
      <div className="flex items-center gap-3 md:gap-4 ml-4 flex-shrink-0 px-4 md:px-8 lg:px-[32px]">
        {/* Notification Bell */}
        <NotificationBell />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-2 md:gap-3 cursor-pointer hover:opacity-80 transition">
              <div className="text-right hidden md:block">
                <p className="text-xs md:text-sm font-medium text-foreground">{user?.first_name || 'User'}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role === 'customer' ? 'Premium Member' : user?.role || 'Member'}
                </p>
              </div>
              <img src={IMG_USER_AVATAR} alt={user?.first_name || 'User'} className="w-8 h-8 md:w-10 md:h-10 rounded-full" />
              <ChevronDown className="w-4 h-4 hidden md:block text-gray-600" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
              {user?.first_name} {user?.last_name}
            </div>
            <DropdownMenuItem asChild>
              <Link to="/dashboard/settings" className="cursor-pointer text-sm">
                <User className="h-4 w-4 mr-2" />
                Account Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive cursor-pointer text-sm"
              onClick={logout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="bg-[#f8f9fb] min-h-screen w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Desktop Sidebar */}
      <SideNavBar />

      {/* Mobile overlay */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30 transition-opacity duration-200"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed left-0 top-0 h-screen w-[280px] bg-white z-40 shadow-2xl transition-transform duration-300 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        onClick={(e) => {
          // close drawer when a nav link inside is clicked (route change)
          const target = e.target as HTMLElement;
          if (target.closest('a')) setDrawerOpen(false);
        }}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <span className="font-bold text-lg text-teal-700">Virewire</span>
            <button
              onClick={() => setDrawerOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* mobile=true strips the desktop `hidden lg:flex` so the nav is actually visible inside the drawer */}
          <SideNavBar mobile />
        </div>
      </div>

      {/* Top Nav */}
      <TopNavBar onMenuClick={() => setDrawerOpen(true)} />

      {/* Main content area */}
      <main className="lg:ml-[288px] pt-[60px] pb-[32px] px-4 md:px-8 lg:px-[32px]">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
